import React from 'react';
import { Box, Container, Typography } from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Box
      sx={{
        bgcolor: '#F5F5F7',
        minHeight: '100vh',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#1D1D1F',
        py: { xs: 6, md: 10 }
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '24px',
            p: { xs: 3, md: 6 },
            boxShadow: '0 20px 60px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.06)'
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 600, mb: 1 }}>
            Privacy Policy
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 3 }}>
            Last Updated: December 23, 2025
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Beck Health, LLC (“Beck Health,” “we,” “us,” or “our”) respects your privacy and is committed to protecting it through this Privacy Policy. This Privacy Policy describes how we collect, use, disclose, and safeguard information when you access or use our websites, applications, products, services, and related offerings, including Aven (collectively, the “Services”).
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            By accessing or using the Services, you acknowledge that you have read, understood, and agree to this Privacy Policy. If you do not agree, do not use the Services.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            1. Scope of This Privacy Policy
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            This Privacy Policy applies to information collected:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Through the Services</li>
            <li>Through websites operated by Beck Health</li>
            <li>Through direct communications with us (email, messaging, customer support)</li>
            <li>Through integrations, tools, or features related to Aven</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            This Privacy Policy does not apply to:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Information collected by third parties not controlled by Beck Health</li>
            <li>Healthcare providers, pharmacies, insurers, or medical professionals who may independently collect your information</li>
            <li>Third-party websites or services linked from our Services</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            2. Important Disclosures
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Beck Health is not a healthcare provider. The Services do not provide medical diagnosis, treatment, or clinical decision-making.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            The Services are designed to provide supportive, informational, and adherence-focused assistance, not medical advice.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Always consult a qualified healthcare professional for medical decisions.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            3. Information We Collect
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            A. Information You Provide Directly
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            We may collect information you voluntarily provide, including:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Name, email address, phone number</li>
            <li>Account credentials</li>
            <li>Communications and messages exchanged with Aven</li>
            <li>Preferences, goals, feedback, and responses</li>
            <li>Payment or billing information (processed by third-party payment processors)</li>
            <li>Customer support inquiries</li>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            B. Information Generated Through Use of the Services
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            We may collect information generated through your interaction with Aven, including:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Conversation content</li>
            <li>Usage patterns</li>
            <li>Engagement data</li>
            <li>Feature interactions</li>
            <li>System-generated insights derived from your inputs</li>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            C. Automatically Collected Information
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            We may automatically collect:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>IP address</li>
            <li>Device identifiers</li>
            <li>Browser type</li>
            <li>Operating system</li>
            <li>Log data</li>
            <li>Cookies and similar technologies</li>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            D. Health-Related Information
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            If you choose to share information related to symptoms, habits, or wellness experiences, such information is collected solely for the purpose of providing Services you request.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            You are not required to provide health-related information to use the Services.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            4. How We Use Information
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            We may use information to:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Provide, operate, and maintain the Services</li>
            <li>Personalize user experiences</li>
            <li>Enable Aven’s conversational and adaptive functionality</li>
            <li>Improve features, performance, and reliability</li>
            <li>Communicate with users</li>
            <li>Respond to inquiries and support requests</li>
            <li>Monitor security and prevent fraud</li>
            <li>Comply with legal obligations</li>
            <li>Protect Beck Health’s rights, property, and safety</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We may also use de-identified and aggregated information for analytics, research, and business purposes.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            5. AI and Automated Processing
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Aven uses automated systems, including artificial intelligence and machine learning models, to generate responses, insights, and guidance based on user inputs.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Automated processing is used to support personalization and functionality.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            No automated decisions are intended to produce legal, medical, or clinical outcomes.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Beck Health does not guarantee accuracy, completeness, or suitability of AI-generated content.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            6. Data Sharing and Disclosure
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We do not sell personal information.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            We may disclose information:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>To service providers and vendors performing services on our behalf</li>
            <li>To comply with legal obligations, subpoenas, or lawful requests</li>
            <li>To enforce our Terms of Service</li>
            <li>To protect rights, safety, and property</li>
            <li>In connection with a merger, acquisition, or asset sale</li>
            <li>With your consent or at your direction</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            All service providers are required to maintain appropriate confidentiality and security protections.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            7. Data Retention
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We retain information only as long as necessary to:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Provide the Services</li>
            <li>Fulfill the purposes described in this Policy</li>
            <li>Comply with legal, regulatory, or contractual obligations</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We may retain de-identified information indefinitely.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            8. Security Measures
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            We implement administrative, technical, and physical safeguards designed to protect information, including:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Encryption</li>
            <li>Access controls</li>
            <li>Secure infrastructure</li>
            <li>Regular monitoring and testing</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            However, no system is completely secure, and we cannot guarantee absolute security.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            9. HIPAA and Health Information
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            To the extent applicable:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Beck Health maintains safeguards consistent with HIPAA-aligned security principles.</li>
            <li>Beck Health is not acting as a healthcare provider or covered entity unless expressly stated.</li>
            <li>Information shared with Aven is used for supportive and informational purposes only.</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            10. Your Choices and Rights
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            Depending on your location, you may have rights to:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Access your information</li>
            <li>Correct inaccuracies</li>
            <li>Request deletion</li>
            <li>Restrict or object to processing</li>
            <li>Withdraw consent</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            Requests may be submitted to:
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            support@beckhealth.com
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We reserve the right to verify identity before fulfilling requests.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            11. Cookies and Tracking Technologies
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            We use cookies and similar technologies to:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Operate the Services</li>
            <li>Improve functionality</li>
            <li>Analyze usage</li>
            <li>Maintain security</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            You may control cookies through your browser settings.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            12. Third-Party Services and Links
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            The Services may contain links or integrations with third-party services. Beck Health is not responsible for the privacy practices or content of third parties.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Use of third-party services is at your own risk.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            13. Children’s Privacy
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            The Services are not intended for individuals under the age of 18. We do not knowingly collect information from minors.
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            If you believe a minor has provided information, contact us immediately at support@beckhealth.com
            <br />
            .
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            14. International Users
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            The Services are operated from the United States. If you access the Services from outside the U.S., you consent to the transfer and processing of information in the United States.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            15. Disclaimer of Warranties
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            The Services are provided “AS IS” and “AS AVAILABLE.”
            <br />
            Beck Health disclaims all warranties related to data accuracy, availability, fitness for purpose, and uninterrupted operation.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            16. Limitation of Liability
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 1, lineHeight: 1.8 }}>
            To the maximum extent permitted by law, Beck Health shall not be liable for:
          </Typography>

          <Box component="ul" sx={{ color: '#424245', pl: 3, mt: 0, mb: 2, lineHeight: 1.8 }}>
            <li>Indirect, incidental, or consequential damages</li>
            <li>Loss of data</li>
            <li>Reliance on AI-generated content</li>
            <li>Unauthorized access beyond reasonable security measures</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            17. Changes to This Privacy Policy
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            We may update this Privacy Policy at any time. Updates will be posted with a revised “Last Updated” date. Continued use of the Services constitutes acceptance of changes.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            18. Contact Information
          </Typography>

          <Typography variant="body1" sx={{ color: '#424245', mb: 2, lineHeight: 1.8 }}>
            Beck Health, LLC
            <br />
            Email: support@beckhealth.com
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
